import { useEffect, useState } from 'react'
import { useHubLang } from '../lib/i18n'
import {
  EMAILS,
  loadMyCommunication,
  setMyCommunication,
  type CommPreference,
  type CommSetting,
  type EmailsCopy,
} from '../lib/communication'

/**
 * #/profile → Emails and notifications (ACC-53, 326).
 *
 * The body takes its data as props so the suite can render it (an effect does
 * not run under renderToString); EmailsSection below loads and saves.
 *
 * Each row offers exactly the settings the server says the category takes —
 * never a setting the platform cannot carry out (standing rule 4): there is
 * no "email me at once" for recognitions because no such email exists. A
 * duty has no "only on the bell" and says why.
 */
export function EmailsBody({
  t,
  prefs,
  saving,
  status,
  onChange,
}: {
  t: EmailsCopy
  prefs: CommPreference[]
  saving: string | null
  status: 'idle' | 'saved' | 'failed'
  onChange: (category: string, setting: CommSetting) => void
}) {
  const weekly = prefs.find((p) => p.category === 'weekly_email')
  const rows = prefs.filter((p) => p.category !== 'weekly_email')
  return (
    <>
      {weekly && (
        <fieldset className="profile__read">
          <legend className="profile__subhead">{t.weeklyLabel}</legend>
          <p className="profile__lede">{t.weeklyHint}</p>
          {/* Choice pills (30 Oct): three short answers read as one row, not
              three lines of white space. Still real radios, so arrow keys
              move between them and a screen reader hears a group. */}
          <div className="profile__choices">
            {weekly.settings.map((s) => (
              <label key={s} className="profile__choice">
                <input
                  type="radio"
                  name="comm-weekly"
                  value={s}
                  checked={weekly.setting === s}
                  disabled={saving !== null}
                  onChange={() => onChange('weekly_email', s)}
                />
                <span>{t.weekly[s as keyof EmailsCopy['weekly']] ?? s}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      {rows.length > 0 && (
        <div className="profile__read">
          <p className="profile__subhead">{t.categoriesHead}</p>
          {/* One row per kind: what it is on the left, the choice on the
              right (stacked in a narrow card). Before 30 Oct each hint sat
              flush against the next row's label and read as belonging to it. */}
          <div className="profile__prefs">
            {rows.map((p) => {
              const words = t.category[p.category] ?? { label: p.category, hint: '' }
              const id = `comm-${p.category}`
              const hint = p.isDuty ? `${words.hint} ${t.dutyNote}`.trim() : words.hint
              return (
                <div key={p.category} className="profile__pref">
                  <div className="profile__preftext">
                    <label className="profile__preflabel" htmlFor={id}>{words.label}</label>
                    {hint && <p id={`${id}-hint`} className="profile__note profile__note--tight">{hint}</p>}
                  </div>
                  <select
                    id={id}
                    className="profile__input"
                    value={p.setting}
                    disabled={saving !== null}
                    aria-describedby={hint ? `${id}-hint` : undefined}
                    onChange={(e) => onChange(p.category, e.target.value as CommSetting)}
                  >
                    {p.settings.map((s) => (
                      <option key={s} value={s}>{t.setting[s as keyof EmailsCopy['setting']] ?? s}</option>
                    ))}
                  </select>
                </div>
              )
            })}
          </div>
        </div>
      )}
      <p className="profile__status" role="status" aria-live="polite">{status === 'saved' ? t.saved : ''}</p>
      {status === 'failed' && <p className="profile__error" role="alert">{t.failed}</p>}
    </>
  )
}

export function EmailsSection() {
  const t = EMAILS[useHubLang()]
  const [prefs, setPrefs] = useState<CommPreference[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'saved' | 'failed'>('idle')

  useEffect(() => {
    let live = true
    loadMyCommunication()
      .then((p) => live && setPrefs(p))
      .catch(() => live && setLoadFailed(true))
    return () => { live = false }
  }, [])

  async function onChange(category: string, setting: CommSetting) {
    if (!prefs) return
    setSaving(category)
    setStatus('idle')
    try {
      await setMyCommunication(category, setting)
      // A duty's answer depends on the Monday email, so read the whole set
      // back rather than patching one row.
      setPrefs(await loadMyCommunication())
      setStatus('saved')
    } catch {
      setStatus('failed')
    } finally {
      setSaving(null)
    }
  }

  return (
    <section className="card profile__card" aria-labelledby="emails-h">
      <h2 id="emails-h" className="card__title">{t.head}</h2>
      <p className="profile__lede">{t.lede}</p>
      {loadFailed && <p className="profile__error" role="alert">{t.loadFailed}</p>}
      {prefs && <EmailsBody t={t} prefs={prefs} saving={saving} status={status} onChange={(c, s) => void onChange(c, s)} />}
    </section>
  )
}
